import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing meta tags and structured data
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.url - Canonical URL
 * @param {Object} props.article - Article-specific data (for blog posts)
 * @param {string} props.type - Page type (default: 'website', use 'article' for blog posts)
 */
export const SEO = ({
  title,
  description,
  image,
  url,
  article,
  type = 'website',
  keywords = [],
}) => {
  const siteTitle = 'Exohaven';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'Web3 community platform with blog, project showcase, and decentralized features';
  const finalDescription = description || defaultDescription;
  const defaultImage = `${window.location.origin}/og-image.jpg`;
  const finalImage = image || defaultImage;
  const finalUrl = url || window.location.href;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={finalUrl} />

      {/* Open Graph Tags (Facebook, LinkedIn, etc.) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title || siteTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={siteTitle} />

      {/* Article-specific Open Graph tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          {article.modifiedAt && <meta property="article:modified_time" content={article.modifiedAt} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={article ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title || siteTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Structured Data (JSON-LD) */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            image: finalImage,
            datePublished: article.publishedAt,
            dateModified: article.modifiedAt || article.publishedAt,
            author: {
              '@type': 'Person',
              name: article.author || 'Exohaven Team',
            },
            publisher: {
              '@type': 'Organization',
              name: siteTitle,
              logo: {
                '@type': 'ImageObject',
                url: `${window.location.origin}/logo.png`,
              },
            },
            description: finalDescription,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': finalUrl,
            },
          })}
        </script>
      )}

      {/* Organization Structured Data (for homepage) */}
      {!article && type === 'website' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: siteTitle,
            url: window.location.origin,
            logo: `${window.location.origin}/logo.png`,
            sameAs: [
              // Add social media URLs here
            ],
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
